/**
 * Database Connection Type Definitions
 *
 * TypeScript types for database connection entities and operations.
 * Mirrors backend models from tenant-api/src/models/index.ts
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Database engine types
 */
export const DatabaseEngine = {
  SQL_SERVER: 'SQL_SERVER',
  MYSQL: 'MYSQL',
  POSTGRESQL: 'POSTGRESQL',
  ORACLE: 'ORACLE',
} as const;

export type DatabaseEngine = typeof DatabaseEngine[keyof typeof DatabaseEngine];

/** @deprecated Use DatabaseEngine instead */
export const DatabaseConnectionType = DatabaseEngine;
/** @deprecated Use DatabaseEngine instead */
export type DatabaseConnectionType = DatabaseEngine;

/**
 * Connection type discriminator
 */
export const ConnectionType = {
  SELF_MANAGED: 'SELF_MANAGED',
  KETRICS_MANAGED: 'KETRICS_MANAGED',
} as const;

export type ConnectionType = typeof ConnectionType[keyof typeof ConnectionType];

// ============================================================================
// DATABASE CONNECTION ENTITY
// ============================================================================

/**
 * Database Connection - External or managed database connection configuration
 * Note: password is NEVER returned in API responses
 */
export interface DatabaseConnection {
  tenantId: string;
  dbConnectionId: string; // UUID v4
  dbConnectionName: string;
  dbConnectionCode: string; // Unique code (e.g., "prod-db", "analytics-db") - immutable after creation
  description?: string;
  isActive: boolean;

  // Connection type discriminator
  connectionType: ConnectionType;

  // Database engine (auto-set to POSTGRESQL for KETRICS_MANAGED)
  engine: DatabaseEngine;

  // Self-managed connection details (required for SELF_MANAGED, absent for KETRICS_MANAGED)
  host?: string;
  port?: number; // 1-65535
  database?: string;
  username?: string;
  // password is NEVER returned in API responses
  ssl?: boolean; // Whether to use SSL/TLS connection

  // Managed SQL DB reference (required for KETRICS_MANAGED, absent for SELF_MANAGED)
  managedSqlDbCode?: string;
  managedSqlDbId?: string;

  // SSH Port Forwarding (optional, SELF_MANAGED only)
  portForwarding?: boolean;
  sshHost?: string;
  sshPort?: number;
  sshUsername?: string;

  // SSH Key Authentication (platform-generated)
  sshPublicKey?: string; // OpenSSH format public key (displayed to user for bastion setup)
  sshKeyFingerprint?: string; // SHA256 fingerprint for identification
  sshKeyGeneratedAt?: string; // ISO timestamp of key generation

  // Metadata
  createdBy: string; // User ID
  createdAt: string;
  updatedAt: string;
}

/**
 * Sanitized database connection (for API responses)
 * Excludes sensitive fields like password
 */
export type DatabaseConnectionSanitized = DatabaseConnection;

// ============================================================================
// DTOs
// ============================================================================

/**
 * DTO for database connection creation
 */
export interface DatabaseConnectionCreateDTO {
  dbConnectionName: string;
  dbConnectionCode: string; // Required: unique code within tenant
  description?: string;
  isActive?: boolean;

  // Connection type discriminator (required)
  connectionType: ConnectionType;

  // Database engine
  engine: DatabaseEngine;

  // Self-managed connection details (required for SELF_MANAGED)
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password?: string; // Plain text (will be encrypted on backend)
  ssl?: boolean; // Whether to use SSL/TLS connection

  // Managed SQL DB reference (required for KETRICS_MANAGED)
  managedSqlDbCode?: string;

  // SSH Port Forwarding (optional, SELF_MANAGED only)
  portForwarding?: boolean;
  sshHost?: string;
  sshPort?: number;
  sshUsername?: string;
}

/**
 * DTO for database connection updates
 * Note: dbConnectionCode and connectionType are immutable and cannot be updated
 */
export interface DatabaseConnectionUpdateDTO {
  dbConnectionName?: string;
  description?: string;
  isActive?: boolean;
  engine?: DatabaseEngine;
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password?: string; // Plain text (will be encrypted on backend)
  ssl?: boolean; // Whether to use SSL/TLS connection
  portForwarding?: boolean;
  sshHost?: string | null; // null to remove
  sshPort?: number | null; // null to remove
  sshUsername?: string | null; // null to remove
}

// ============================================================================
// QUERY TYPES
// ============================================================================

/**
 * Database connection list query parameters
 */
export interface DatabaseConnectionListQuery {
  search?: string;
  engine?: DatabaseEngine;
  connectionType?: ConnectionType;
  isActive?: boolean;
  limit?: number;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * Database connection list response
 */
export interface DatabaseConnectionListResponse {
  databaseConnections: DatabaseConnection[];
  count: number;
  lastEvaluatedKey?: Record<string, unknown>;
}
